/**
 * 전역 토스트 – 브랜드 카드형(좌측 accent 바 + 아이콘). 의존성 없음.
 * 스타일: resources/scss/home/components/_toast.scss (home·admin 공용, 인라인 style 미사용).
 * 서버 flash 메시지 및 클라이언트에서 전역 사용.
 */

const DURATION_MS = 3500;
const LEAVE_MS = 220;
const CONTAINER_ID = "dpr-toast-container";

type ToastType = "default" | "success" | "error" | "warning" | "info" | "loading";

export interface ToastHandle {
  success(message?: string): void;
  error(message?: string): void;
  dismiss(): void;
}

interface ToastShowOptions {
  id?: string;
  durationMs?: number;
  persist?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const ICONS: Record<ToastType, string> = {
  default: "fa-solid fa-circle-info",
  success: "fa-solid fa-circle-check",
  error: "fa-solid fa-circle-xmark",
  warning: "fa-solid fa-triangle-exclamation",
  info: "fa-solid fa-circle-info",
  loading: "fa-solid fa-spinner fa-spin",
};

const removalTimers = new WeakMap<HTMLElement, number>();
const leaveTimers = new WeakMap<HTMLElement, number>();

function getContainer(): HTMLDivElement {
  let el = document.getElementById(CONTAINER_ID) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = CONTAINER_ID;
    el.className = "ui-toast-container";
    document.body.appendChild(el);
  }
  return el;
}

function findToastById(container: HTMLElement, id: string): HTMLDivElement | null {
  for (const child of Array.from(container.children)) {
    if (child instanceof HTMLDivElement && child.dataset.toastId === id) {
      return child;
    }
  }
  return null;
}

function clearToastTimers(toast: HTMLElement): void {
  const removalTimer = removalTimers.get(toast);
  if (removalTimer) {
    window.clearTimeout(removalTimer);
    removalTimers.delete(toast);
  }

  const leaveTimer = leaveTimers.get(toast);
  if (leaveTimer) {
    window.clearTimeout(leaveTimer);
    leaveTimers.delete(toast);
  }
}

function removeToast(toast: HTMLElement): void {
  clearToastTimers(toast);
  toast.classList.add("is-leaving");
  const leaveTimer = window.setTimeout(() => {
    toast.remove();
    leaveTimers.delete(toast);
  }, LEAVE_MS);
  leaveTimers.set(toast, leaveTimer);
}

function renderToast(toast: HTMLDivElement, message: string, type: ToastType, options: ToastShowOptions = {}): void {
  toast.className = `ui-toast ui-toast--${type}`;
  toast.textContent = "";
  const isAssertive = type === "error" || type === "warning";
  toast.setAttribute("role", isAssertive ? "alert" : "status");
  toast.setAttribute("aria-live", isAssertive ? "assertive" : "polite");
  toast.setAttribute("aria-atomic", "true");

  const icon = document.createElement("span");
  icon.className = "ui-toast__icon";
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML = `<i class="${ICONS[type]}"></i>`;

  const msg = document.createElement("span");
  msg.className = "ui-toast__msg no-body-sm";
  msg.textContent = message; // XSS 안전

  toast.appendChild(icon);
  toast.appendChild(msg);

  if (options.action) {
    const action = document.createElement("button");
    action.type = "button";
    action.className = "ui-toast__action";
    action.textContent = options.action.label;
    action.addEventListener("click", () => {
      removeToast(toast);
      options.action?.onClick();
    });
    toast.appendChild(action);
  }
}

function show(message: string, type: ToastType = "default", options: ToastShowOptions = {}): HTMLDivElement {
  const container = getContainer();

  const toast = options.id
    ? findToastById(container, options.id) ?? document.createElement("div")
    : document.createElement("div");

  if (options.id) {
    toast.dataset.toastId = options.id;
  }
  clearToastTimers(toast);
  toast.classList.remove("is-leaving");
  renderToast(toast, message, type, options);

  if (!toast.parentElement) {
    container.appendChild(toast);
  }

  if (!options.persist) {
    const duration = options.durationMs ?? DURATION_MS;
    const removalTimer = window.setTimeout(() => removeToast(toast), duration);
    removalTimers.set(toast, removalTimer);
  }

  return toast;
}

export const Toast = {
  success(message: string, options: ToastShowOptions = {}): void {
    show(message, "success", options);
  },
  error(message: string): void {
    show(message, "error");
  },
  warning(message: string): void {
    show(message, "warning");
  },
  info(message: string): void {
    show(message, "info");
  },
  /** 기본(neutral) */
  show(message: string): void {
    show(message, "default");
  },
  loading(message = "저장중...", id = "form-submit"): ToastHandle {
    const toast = show(message, "loading", { id, persist: true });

    return {
      success(successMessage = "저장완료"): void {
        show(successMessage, "success", { id });
      },
      error(errorMessage = "저장 실패"): void {
        show(errorMessage, "error", { id });
      },
      dismiss(): void {
        removeToast(toast);
      },
    };
  },
};

declare global {
  interface Window {
    __FLASH_MESSAGES__?: {
      success?: string | null;
      error?: string | null;
      warning?: string | null;
      info?: string | null;
      errors?: unknown;
      reset_token?: unknown;
    };
  }
}

/** 페이지 로드 시 서버에서 넘긴 flash를 토스트로 표시 (admin 레이아웃 등에서 호출) */
export function showFlashToasts(): void {
  const flash = window.__FLASH_MESSAGES__;
  if (!flash) return;
  if (flash.success) Toast.success(flash.success);
  if (flash.error) Toast.error(flash.error);
  if (flash.warning) Toast.warning(flash.warning);
  if (flash.info) Toast.info(flash.info);
  window.__FLASH_MESSAGES__ = undefined;
}
